import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { User } from 'app/classes/user';
import { FileService } from 'app/core/services/file.service';
import { UserManagementGuard } from 'app/core/services/user-management.guard';
import { GuidedTourStatus } from 'app/guided-tour/models/guided-tour-state.model';
import { AuthService } from 'app/services/auth.service';
import { OfflineService } from 'app/services/offline.service';
import { UserDatabaseService } from 'app/services/user-database.service';
import { environment } from 'environments/environment';
import { DocumentDatabaseService, PlanMetadata } from 'fitforce-document-sync';
import { some } from 'lodash';
import * as moment from 'moment';
import { GuidedTourService } from 'ngx-guided-tour';
import { Observable, Subscription } from 'rxjs';
import * as GuidedTourActions from '../../guided-tour/state/guided-tour.actions';
import { GuidedTourSelectors } from '../../guided-tour/state/guided-tour.selectors';
import { AlertDialogComponent } from '../alert-dialog/alert-dialog.component';
import { ChangePasswordDialogComponent } from '../change-password-dialog/change-password-dialog.component';
import { ImportDatabaseDialogComponent } from '../import-database-dialog/import-database-dialog.component';

@Component({
  selector: 'app-nav-user-menu',
  templateUrl: './nav-user-menu.component.html',
  styleUrls: ['./nav-user-menu.component.scss'],
})
export class NavUserMenuComponent implements OnInit, OnDestroy {
  @Input() currentUser: User;
  @Input() offlinePlanPopover;

  isOnline: boolean;
  supportUrl = environment.externalSupportUrl;
  guidedTourToggledOn = false;

  guidedTourSubscription: Subscription;

  @Select(GuidedTourSelectors.toggleStatus)guidedTourToggleStatus$: Observable<boolean>;

  constructor(
    public authService: AuthService,
    private router: Router,
    private dialog: MatDialog,
    private userDatabaseService: UserDatabaseService,
    private documentDatabaseService: DocumentDatabaseService,
    private fileService: FileService,
    private offline: OfflineService,
    private guidedTourService: GuidedTourService,
    private store: Store,
    private userManagementGuard: UserManagementGuard
  ) {}

  ngOnInit() {
    this.isOnline = this.offline.isOnline();
    this.offline.status.subscribe(value => {
      this.isOnline = value;
    });

    this.guidedTourSubscription = this.store.dispatch(new GuidedTourActions.GetGuidedTourToggleStatus()).subscribe(state => {
      if (state !== undefined) {
        this.guidedTourToggledOn = state.guidedTour.toggleOn;
      }
    });
  }

  /**
   * Determine when to show which nav tabs to display depending if the user is logged in and the route of the page.
   */
  loggedIn() {
    return this.authService.isLoggedIn && this.router.url.indexOf('login') < 0 && this.router.url !== '/';
  }

  isAdmin() {
    return this.currentUser ? !!this.currentUser.isAdmin : false;
  }

  canManageUsers(): boolean {
    return this.currentUser ? this.userManagementGuard.canEdit(this.currentUser) : false;
  }

  /**
   * Call logout on the auth service.
   *
   * @param  {} loggedIn - boolean to check if user is currently logged in
   */
  logout(loggedIn) {
    if (loggedIn) {
      this.offline.getAll(PlanMetadata).subscribe(plans => {
        // if any known plans in memory are dirty, show the message
        const showConfirmationMessage = some(plans, record => record.isDirty);

        // let the browser know if we want to try and stay open
        if (showConfirmationMessage) {
          const confirmed = confirm(
            `You currently have unsynced changes, which may be lost if you logout? Are you sure you want to logout?`
          );
          if (confirmed) {
            this.authService.logout();
          }
        } else {
          this.authService.logout();
        }
      });
    }
  }

  exportDatabase() {
    const dateStr = moment().utc().format();
    this.userDatabaseService.exportToJSON().then(data => {
      this.fileService.saveFile([JSON.stringify(data)], `fitforce-db-(${dateStr}).json`, 'application/json');
    });

    this.documentDatabaseService.exportToJSON().then(data => {
      this.fileService.saveFile([JSON.stringify(data)], `fitforce-db-documents-(${dateStr}).json`, 'application/json');
    });
  }

  importDatabase() {
    this.dialog.open(ImportDatabaseDialogComponent, { width: '600px' });
  }

  /**
   * Open the modal to change the password of the account. Display the right message after the save
   * occurs.
   */
  changePassword() {
    this.dialog
      .open(ChangePasswordDialogComponent, <MatDialogConfig> {
        width: '400px',
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.authService.changePassword(result.current, result.newPW).subscribe(
            (response: any) => {
              if (response['status'].indexOf('success') > -1) {
                this.dialog.open(AlertDialogComponent, <MatDialogConfig> {
                  data: {
                    title: 'Success',
                    content: 'Password successfully changed.',
                  },
                });
              }
            },
            e => {
              this.dialog.open(AlertDialogComponent, <MatDialogConfig> {
                data: {
                  title: 'Unsuccessful',
                  content: 'The original password is incorrect.',
                },
              });
            },
          );
        }
      });
  }

  startGuidedTour(): void {
    this.guidedTourToggledOn = true;
    const newToggleStatus = GuidedTourStatus.ON;
    this.store.dispatch(new GuidedTourActions.SetGuidedTourToggleStatus(newToggleStatus));
  }

  disableGuidedTour(): void {
    this.guidedTourToggledOn = false;
    const newToggleStatus = GuidedTourStatus.OFF;
    this.store.dispatch(new GuidedTourActions.SetGuidedTourToggleStatus(newToggleStatus));
    this.guidedTourService.resetTour();
  }

  openSupport() {
    window.open(this.supportUrl, '_blank');
  }

  ngOnDestroy(): void {
    if (this.guidedTourSubscription) {
      this.guidedTourSubscription.unsubscribe();
    }
  }
}
